import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ImageProject {
  id: string;
  name: string;
  description?: string;
  image_ids: string[];
  tags: string[];
  is_public: boolean;
  share_token?: string;
  created_at: string;
  updated_at: string;
}

export const useImageProjects = () => {
  const [projects, setProjects] = useState<ImageProject[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProjects([]);
        return;
      }

      const { data, error } = await supabase
        .from('image_projects')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (
    name: string,
    description?: string,
    tags: string[] = []
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('image_projects')
        .insert({
          user_id: user.id,
          name,
          description,
          tags,
          image_ids: [],
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Project created successfully',
      });

      await fetchProjects();
      return data;
    } catch (error) {
      console.error('Error creating project:', error);
      toast({
        title: 'Error',
        description: 'Failed to create project',
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateProject = async (
    id: string,
    updates: Partial<Omit<ImageProject, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ) => {
    try {
      const { error } = await supabase
        .from('image_projects')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Project updated successfully',
      });

      await fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
      toast({
        title: 'Error',
        description: 'Failed to update project',
        variant: 'destructive',
      });
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('image_projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Project deleted successfully',
      });

      await fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete project',
        variant: 'destructive',
      });
    }
  };

  const addImagesToProject = async (projectId: string, imageIds: string[]) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error('Project not found');

      const updatedImageIds = [...new Set([...project.image_ids, ...imageIds])];

      await updateProject(projectId, { image_ids: updatedImageIds });
    } catch (error) {
      console.error('Error adding images to project:', error);
      toast({
        title: 'Error',
        description: 'Failed to add images to project',
        variant: 'destructive',
      });
    }
  };

  const removeImagesFromProject = async (projectId: string, imageIds: string[]) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) throw new Error('Project not found');

      const updatedImageIds = project.image_ids.filter(id => !imageIds.includes(id));

      await updateProject(projectId, { image_ids: updatedImageIds });
    } catch (error) {
      console.error('Error removing images from project:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove images from project',
        variant: 'destructive',
      });
    }
  };

  const generateShareToken = async (projectId: string) => {
    try {
      const shareToken = crypto.randomUUID();
      await updateProject(projectId, { 
        is_public: true, 
        share_token: shareToken 
      });
      return shareToken;
    } catch (error) {
      console.error('Error generating share token:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate share link',
        variant: 'destructive',
      });
      return null;
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  return {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    addImagesToProject,
    removeImagesFromProject,
    generateShareToken,
    refetch: fetchProjects,
  };
};